import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AnalysePage } from "./pages/analyse-page";
import type { LocationSuggestion } from "./lib/types";

interface ApplicationProps {
  /**
   * Server-resolved Location, set when the visitor lands on
   * /outils/analyse-de-local/emplacement/:slug. When present, the page
   * skips the autocomplete and auto-fires the analysis on mount.
   */
  preselectedLocation?: LocationSuggestion;
  /**
   * Soft error from the server-side slug resolution (e.g. the Location
   * exists but isn't geolocated). Rendered inline above the form.
   */
  preselectError?: string;
  /**
   * Server-side pre-flight: no published `qr_code` agent is configured
   * for `profile_class = "Location"`. The page renders a banner and
   * suppresses the auto-fire / submit path so the visitor doesn't see
   * a 422 loop.
   */
  agentNotConfigured?: boolean;
}

export function Application({
  preselectedLocation,
  preselectError,
  agentNotConfigured = false,
}: ApplicationProps) {
  // The same React tree handles both URLs (bare /outils/analyse-de-local
  // AND /outils/analyse-de-local/emplacement/:slug). The slug is resolved
  // server-side; here we only care whether a preselected payload was
  // handed to us, so the same <AnalysePage> branches on its props.
  //
  // No quota state is passed in — the form always renders on landing.
  // The QuotaExhausted upsell only appears AFTER a 403 from the
  // analyse endpoint (handled inside AnalysePage's catch block).
  const page = (
    <AnalysePage
      preselectedLocation={preselectedLocation}
      preselectError={preselectError}
      agentNotConfigured={agentNotConfigured}
    />
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/outils/analyse-de-local" element={page} />
        <Route path="/outils/analyse-de-local/emplacement/:slug" element={page} />
        {/* Fallback for any future sub-path — never crashes the SPA. */}
        <Route path="*" element={page} />
      </Routes>
    </BrowserRouter>
  );
}

export default Application;
